import { createRootRoute, Outlet } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createIdbPersister } from '@/lib/idbPersister'
import { PERSIST_MAX_AGE_MS, applyPersistGcTime, shouldPersistQuery } from '@/lib/persistedQueries'
import { hasSilentPrintShell } from '@/lib/offline/shell'
import { OfflineIndicator } from '@/components/OfflineIndicator'
import { useBackendProbe } from '@/lib/offline/probe'
import { DesktopBranding } from '@/components/DesktopBranding'
import { DesktopMinimizeButton } from '@/components/DesktopMinimizeButton'
import { DesktopUpdateBanner } from '@/components/DesktopUpdateBanner'
import { WebUpdateBanner } from '@/components/WebUpdateBanner'
import '../index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
})

// Offline-capable shells only, matching the persister below: a persisted read
// must outlive the default 5-minute gcTime or it is dropped from the next
// IndexedDB write (F-FE-N115). Which keys are persisted lives in
// lib/persistedQueries.ts.
if (hasSilentPrintShell()) applyPersistGcTime(queryClient)

// IndexedDB persister — the menu snapshot (products carry multi-MB base64
// image_urls) blows localStorage's ~5 MB quota, so we persist to IndexedDB.
// Persistence is activated ONLY inside a shell that can sell offline — the
// desktop app and, since F-FE-N123, the Bhookly Server Android app (see the
// component below) — so the live web tenants keep exactly today's in-memory
// behaviour. It was desktop-only before N123, which meant a tablet till that
// reloaded mid-outage came back with no menu while its banner said "showing
// last saved menu".
const persister =
  typeof window !== 'undefined' ? createIdbPersister('bhookly-rq-cache') : null

/**
 * Polls the backend while the app believes it is unreachable (F-FE-N71).
 *
 * Mounted at the ROOT, not on the counter. Any screen can be the one that is
 * open when the link dies, and the screens without a polling query of their own
 * are exactly the ones that could never notice it coming back — reachability
 * used to recover only as a side effect of unrelated traffic. Since the offline
 * queue drains on that same offline->online edge, a screen that never recovered
 * also never synced the orders the kitchen had already cooked.
 *
 * A component rather than a hook call in Route.component so it sits INSIDE the
 * QueryClientProvider tree and renders nothing.
 */
function BackendProbe() {
  useBackendProbe()
  return null
}

export const Route = createRootRoute({
  component: () => {
    const inner = (
      <>
        <div className="min-h-screen bg-background">
          <Outlet />
        </div>
        <BackendProbe />
        <OfflineIndicator />
        <DesktopBranding />
        <DesktopUpdateBanner />
        <WebUpdateBanner />
        <DesktopMinimizeButton />
        <ReactQueryDevtools initialIsOpen={false} />
      </>
    )

    // Desktop app and Server APK: persist reference reads so the UI is usable
    // offline. No tenant-isolation step is needed on Android — the WebView's
    // storage is per-origin and each tenant is its own origin, unlike the
    // desktop shell, which serves every tenant from 127.0.0.1 and must wipe.
    if (persister && hasSilentPrintShell()) {
      return (
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            persister,
            maxAge: PERSIST_MAX_AGE_MS,
            dehydrateOptions: { shouldDehydrateQuery: shouldPersistQuery },
          }}
        >
          {inner}
        </PersistQueryClientProvider>
      )
    }

    // Browser (all live web tenants): unchanged in-memory client.
    return <QueryClientProvider client={queryClient}>{inner}</QueryClientProvider>
  },
})
